import { z } from "zod";

import { richTextToPlainText } from "~/components/ui/rich-text-editor";

import { GoalStatus, GoalTimeline } from "../../../drizzle/schema";

import { CATEGORY_NAME_MAX_LENGTH } from "./category-of-life";
import { encryptedField, encryptionIv } from "./encrypted-field";
import { KeyGrantsSchema } from "./key-grants";

export const GOAL_TITLE_MAX_LENGTH = 100;
export const GOAL_DESCRIPTION_MAX_LENGTH = 255;
export const MILESTONE_TITLE_MAX_LENGTH = 255;

// Shared validation for rich text content
const richTextDescriptionValidation = z
  .string()
  .optional()
  .refine(
    (value) => {
      if (!value) return true;
      const plainText = richTextToPlainText(value);
      // Tiptap CharacterCount ignores newlines between blocks, so we strip them for validation
      return plainText.replace(/\n/g, "").length <= GOAL_DESCRIPTION_MAX_LENGTH;
    },
    {
      message: `Description must be ${GOAL_DESCRIPTION_MAX_LENGTH} characters or less`,
    },
  );

export const GetGoalOfLife = z.object({
  filter: z.object({
    status: z.nativeEnum(GoalStatus).optional(),
    categoryId: z.string().optional(),
    timeline: z.nativeEnum(GoalTimeline).optional(),
  }),
});

/** The browser makes the id, so the goal is created once, with its keys. */
export const CreateGoalOfLife = z.object({
  id: z.string().uuid(),
  categoryId: z.string().uuid(),
  timeline: z.nativeEnum(GoalTimeline),
  encryptedTitle: encryptedField(),
  encryptedDescription: encryptedField(),
  iv: encryptionIv,
  keyGrants: KeyGrantsSchema,
});

/** The goal's text and place. Its state changes only through `SetGoalOfLifeDone`. */
export const UpdateGoalOfLife = z.object({
  id: z.string().uuid(),
  categoryId: z.string().uuid().optional(),
  timeline: z.nativeEnum(GoalTimeline).optional(),
  encryptedTitle: encryptedField(),
  encryptedDescription: encryptedField(),
  iv: encryptionIv,
  keyGrants: KeyGrantsSchema,
});

/** The owner marks the goal done (`done: true`) or reopens it. */
export const SetGoalOfLifeDone = z.object({
  id: z.string().uuid(),
  done: z.boolean(),
});

export const DeleteGoalOfLife = z.object({
  id: z.string().uuid(),
});

export const GetSharedGoalsSchema = z.object({
  userId: z.string().uuid(),
  filter: z.object({
    status: z.nativeEnum(GoalStatus).optional(),
    categoryId: z.string().optional(),
    timeline: z.nativeEnum(GoalTimeline).optional(),
  }),
});

/**
 * The goal form. The client picks one of her categories (`categoryId`) or
 * names a new one (`newCategoryName`), which is created with the goal.
 * Empty first steps are left out.
 */
export const GoalFormSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1, "Name what you want to reach")
      .max(
        GOAL_TITLE_MAX_LENGTH,
        `Keep it to ${GOAL_TITLE_MAX_LENGTH} characters`,
      ),
    categoryId: z.string(),
    newCategoryName: z
      .string()
      .trim()
      .max(
        CATEGORY_NAME_MAX_LENGTH,
        `Keep it to ${CATEGORY_NAME_MAX_LENGTH} characters`,
      ),
    timeline: z.nativeEnum(GoalTimeline),
    description: richTextDescriptionValidation,
    firstSteps: z.array(
      z
        .string()
        .trim()
        .max(
          MILESTONE_TITLE_MAX_LENGTH,
          `Keep a step to ${MILESTONE_TITLE_MAX_LENGTH} characters`,
        ),
    ),
  })
  .superRefine((form, ctx) => {
    if (!form.categoryId && !form.newCategoryName) {
      ctx.addIssue({
        code: "custom",
        path: ["categoryId"],
        message: "Pick a category",
      });
    }
  });

export type GoalFormData = z.input<typeof GoalFormSchema>;
